import { Component, OnInit, OnDestroy, Inject } from '@angular/core';
import { Location, LocationStrategy, PathLocationStrategy, PlatformLocation } from '@angular/common';
import { MsalService, MsalBroadcastService, MSAL_GUARD_CONFIG, MsalGuardConfiguration } from '@azure/msal-angular';
import { InteractionStatus, RedirectRequest, AuthenticationResult, SilentRequest } from '@azure/msal-browser';
import { Subject } from 'rxjs';
import { filter, takeUntil } from 'rxjs/operators';
import { Router } from '@angular/router';
import { environment } from 'src/environments/environment';
import { APP_CONFIG } from 'src/app/core/services/app-config.service';
import { AppConfig } from 'src/app/shared/models/config-variable.model';
import { ToastService } from 'src/app/core/services/toast.service';
import { MessageService } from 'src/app/core/services/message.service';

@Component({
  selector: 'app-root',
  templateUrl: './app.component.html',
  styleUrls: ['./app.component.scss'],
  providers: [Location, { provide: LocationStrategy, useClass: PathLocationStrategy }]
})
export class AppComponent implements OnInit, OnDestroy {
  title = 'Mobility Plan';
  isIframe = false;
  loginDisplay = false;
  private readonly _destroying$ = new Subject<void>();

  constructor(
    @Inject(MSAL_GUARD_CONFIG) private msalGuardConfig: MsalGuardConfiguration,
    private broadcastService: MsalBroadcastService,
    private authService: MsalService,
    @Inject(APP_CONFIG) private config: AppConfig,
    private location: PlatformLocation,
    private toastService: ToastService,
    public msg: MessageService,
    private router: Router
  ) { }

  ngOnInit() {
    this.isIframe = window !== window.parent && !window.opener;

    this.broadcastService.inProgress$
      .pipe(
        filter((status: InteractionStatus) => status === InteractionStatus.None),
        takeUntil(this._destroying$)
      )
      .subscribe(() => {
        this.setLoginDisplay();
        if (!this.loginDisplay) {
          this.login();
        } else {
          this.acquireToken();
        }
      });

    // Handle redirect responses
    this.authService.handleRedirectObservable().subscribe({
      next: (response: AuthenticationResult) => {
        if (response) {
          console.log('Login successful:', response);
          this.authService.instance.setActiveAccount(response.account);
          this.acquireToken();
        }
      },
      error: (err) => {
        console.error('Redirect error:', err);
      }
    });

    // 🔄 Periodic token refresh every 50 minutes
    setInterval(() => {
      if (this.loginDisplay) {
        this.acquireToken();
      }
    }, 50 * 60 * 1000);
  }

  /** Normalize authRequest into a usable request object */
  private getAuthRequest(): RedirectRequest & SilentRequest {
    let request: any;

    if (typeof this.msalGuardConfig.authRequest === 'function') {
      // Call the function safely without RouterStateSnapshot
      request = this.msalGuardConfig.authRequest(this.authService, {} as any);
    } else {
      request = this.msalGuardConfig.authRequest || {};
    }

    return {
      scopes: request.scopes || [this.config.ApiScopeURL],
      redirectStartPage: window.location.href
    };
  }

  login() {
    const loginRequest = this.getAuthRequest();
    this.authService.loginRedirect(loginRequest);
  }

  logout() {
    this.authService.logoutRedirect();
  }

  setLoginDisplay() {
    this.loginDisplay = this.authService.instance.getAllAccounts().length > 0;
  }

  acquireToken() {
    const request = this.getAuthRequest();

    this.authService.acquireTokenSilent(request).subscribe({
      next: (response) => {
        localStorage.setItem('token', response.accessToken);
      },
      error: (err) => {
        console.log('Token acquisition failed:', err);
        if (err.errorCode === 'monitor_window_timeout' || err.name === 'InteractionRequiredAuthError') {
          console.log('Falling back to acquireTokenRedirect with request ', request);
          this.authService.acquireTokenRedirect(request);
        }
      }
    });
  }

  ngOnDestroy(): void {
    this._destroying$.next(undefined);
    this._destroying$.complete();
  }
}
